import { Link } from 'react-router-dom'
import { useAlertList } from '@/lib/useAlerts'
import type { Alert } from '@/types/alert'

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  warning: 1,
  info: 2,
}

const SEVERITY_ICON: Record<string, string> = {
  critical: '🔴',
  warning: '🟡',
  info: '🔵',
}

function formatRelativeTime(isoDate: string): string {
  const created = new Date(isoDate)
  const now = new Date()
  const diffMs = now.getTime() - created.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'heute'
  if (diffDays === 1) return 'gestern'
  return `vor ${diffDays} Tagen`
}

function sortBySeverity(items: Alert[]): Alert[] {
  return [...items].sort((a, b) => {
    const sevDiff = (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99)
    if (sevDiff !== 0) return sevDiff
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
}

export function RemindersWidget() {
  const query = useAlertList({
    category: 'reminder',
    unread_only: true,
    limit: 5,
  })

  const items = sortBySeverity(query.data?.items ?? [])
  const count = query.data?.total ?? 0

  return (
    <section className="border border-hairline p-6">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="tracked-caps text-[10px] text-faint">Erinnerungen</p>
          <h3 className="mt-2 text-lg text-ink">
            Reminder{count > 0 && <span className="ml-2 text-muted">· {count}</span>}
          </h3>
        </div>
        <Link
          to="/alerts?tab=reminder"
          className="tracked-caps text-[10px] text-bernstein hover:underline"
        >
          Alle Reminder
        </Link>
      </header>

      <div className="mt-6">
        {query.isLoading ? (
          <p className="font-mono text-sm text-muted">Lade…</p>
        ) : items.length === 0 ? (
          <p className="font-mono text-sm text-faint">Keine offenen Reminder</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {items.map((alert) => (
              <li key={alert.id} className="flex items-start gap-3">
                <span aria-hidden="true">{SEVERITY_ICON[alert.severity] ?? '•'}</span>
                <div className="flex-1">
                  <p className="font-mono text-sm text-ink">{alert.title}</p>
                  <p className="font-mono text-xs text-faint">
                    {formatRelativeTime(alert.created_at)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
